import { Box, Button, Flex, FormGroup, FormLabel, Grid, Inline, Paragraph, Stack } from "@eworkit/iris";
import { FormikProps } from "formik";
import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { FormikTextInput, VALIDATION_TYPES } from "@/components/Formik";
import { FormikError } from "@/components/Formik";
import { ILocation } from "@/data/locations";
import { FormikForm } from "@/formikComponents";
import { RouteButton } from "@/infrastructure/Route";
import { Messenger } from "@/views/components/Messenger";
import { router } from "@/views/router";

import {
  AgreementsForm,
  AGREEMENTS_FORM_INIT_VALUES,
  IAgreementsFormValues,
  validationSchema as agreementsValidationSchema,
} from "./Agreements";
import { PasswordValidation, passwordValidationSchema, usePasswordValidation } from "./PasswordValidation";

export const validationSchema = Yup.object()
  .shape({
    email: Yup.string()
      .required(VALIDATION_TYPES.REQUIRED)
      .matches(/^[a-zA-Z\d._-]+@[a-zA-Z\d][a-zA-Z\d-.]{0,61}[a-zA-Z\d]\.[a-zA-Z]{2,}$/, VALIDATION_TYPES.EMAIL),
    firstName: Yup.string().required(VALIDATION_TYPES.REQUIRED),
    lastName: Yup.string().required(VALIDATION_TYPES.REQUIRED),
    password: Yup.string().concat(passwordValidationSchema),
    confirmPassword: Yup.string()
      .required(VALIDATION_TYPES.REQUIRED)
      .oneOf([Yup.ref("password"), ""], VALIDATION_TYPES.FIELDS_NOT_MATCH),
  })
  .concat(agreementsValidationSchema);

export const FORM_MESSENGER_CONTEXT = "user-form";

const EMAIL_MAX_LENGTH = 100;

export const FORM_EMPTY_INITIAL_VALUES = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
  ...AGREEMENTS_FORM_INIT_VALUES,
};

export interface ISignUpUserFormValues extends IAgreementsFormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  location?: ILocation;
}

interface Props {
  form: FormikProps<ISignUpUserFormValues>;
  hideEmailInput?: boolean;
  onCancel?: () => void;
}

export const SignUpUserForm: FC<Props> = memo(props => {
  const { hideEmailInput, form } = props;
  const passwordErrors = usePasswordValidation(form.values.password);
  const { t } = useTranslation("public");

  return (
    <FormikForm>
      <Stack gap="md">
        <Stack gap="sm">
          <Messenger context={FORM_MESSENGER_CONTEXT} />

          <Grid columns={["1fr", null, "1fr 1fr"]}>
            <Box>
              <FormGroup
                label={<FormLabel label={t("SESSION_SIGNUP.USER_FORM.PLACEHOLDER_FIRST_NAME")} required />}
                error={<FormikError name="firstName" />}>
                <FormikTextInput type="text" name="firstName" value={form.values.firstName} size="lg" />
              </FormGroup>
            </Box>

            <Box>
              <FormGroup
                label={<FormLabel label={t("SESSION_SIGNUP.USER_FORM.PLACEHOLDER_LAST_NAME")} required />}
                error={<FormikError name="lastName" />}>
                <FormikTextInput type="text" name="lastName" value={form.values.lastName} size="lg" />
              </FormGroup>
            </Box>
          </Grid>

          {!hideEmailInput && (
            <FormGroup
              label={<FormLabel label={t("SESSION_SIGNUP.USER_FORM.PLACEHOLDER_EMAIL")} required />}
              error={
                !hideEmailInput && (
                  <FormikError
                    name="email"
                    overrideMessages={{
                      NOT_UNIQUE: t("SESSION_SIGNUP.USER_FORM.ERROR_MESSAGES.EMAIL_NOT_UNIQUE"),
                      INVALID_EMAIL: t("common:VALIDATION_MESSAGES.MAX_LENGTH", { value: EMAIL_MAX_LENGTH }),
                    }}
                  />
                )
              }>
              <FormikTextInput name="email" value={form.values.email} required size="lg" />
            </FormGroup>
          )}

          <Grid columns={["1fr", null, "1fr 1fr"]}>
            <Stack gap="sm">
              <FormGroup
                label={<FormLabel label={t("SESSION_SIGNUP.USER_FORM.PLACEHOLDER_PASSWORD")} required />}
                error={
                  <FormikError
                    name="password"
                    overrideMessages={{
                      PASSWORD_BAD_CHAR: t("SESSION_SIGNUP.USER_FORM.ERROR_MESSAGES.PASSWORD_BAD_CHAR"),
                    }}
                  />
                }>
                <FormikTextInput value={form.values.password} name="password" size="lg" type="password" />
              </FormGroup>
              <FormGroup
                label={<FormLabel label={t("SESSION_SIGNUP.USER_FORM.PLACEHOLDER_CONFIRM_PASSWORD")} required />}
                error={<FormikError name="confirmPassword" />}>
                <FormikTextInput value={form.values.confirmPassword} name="confirmPassword" size="lg" type="password" />
              </FormGroup>
            </Stack>

            <Box pl="sm" pt="md">
              <PasswordValidation errors={passwordErrors} />
            </Box>
          </Grid>
        </Stack>

        <AgreementsForm form={form} />

        <Button type="submit" disabled={form.isSubmitting} size="lg">
          {t("SESSION_SIGNUP.USER_FORM.BUTTON_CREATE_ACCOUNT")}
        </Button>

        <Flex justifyContent="center" py="xs">
          <Inline gap="xxs">
            <Paragraph>{t("SESSION_LOGIN.SWITCH.TEXT")}</Paragraph>

            <RouteButton
              variant="link"
              display="inline-block"
              path={router.get("login").path}
              className="sign-up-user-form">
              {t("SESSION_LOGIN.SWITCH.LINK")}
            </RouteButton>
          </Inline>
        </Flex>
      </Stack>
    </FormikForm>
  );
});
